// src/components/chat/WelcomeScreen.tsx
//
// Empty-state for /chat with no active conversation. Lets the user
// pick the mode their new conversation will run in:
//
//   • Standard      — open chat, works anywhere.
//   • Walking tour  — fires the dedicated walking-tour generator
//                     (existing Cloud Function) once the user types
//                     their focus. The user's first message IS the
//                     focus.
//   • Museum guide  — sets up an in-chat onboarding state machine.
//                     The chat AI then asks for the museum and the
//                     guided/free-roam choice via FOLLOW_UP_QUESTIONS
//                     chips. No modal.
//
// Mode descriptions are baked into this component intentionally —
// they're a UI feature of the new-conversation screen, not content
// that the AI needs to know about. They go through useT() so the
// translation pipeline picks them up.
//
// The user "selects" a mode by tapping a card. Selected mode is
// captured in parent state via onSelectMode and survives until the
// user either sends (committing the mode to the new conversation) or
// navigates away. Standard is the implicit default — no card needs to
// be tapped to chat normally.

import React from 'react';
import { Box, Typography, Stack, ButtonBase } from '@mui/material';
import {
  ChatBubbleOutline,
  MapOutlined,
  AccountBalanceOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import type { ConversationType } from '../../stores/conversationStore';
import { useT } from '../../i18n/useT';

interface WelcomeScreenProps {
  /** Currently-selected mode, or null for "no choice yet" (which the
   *  parent treats as Standard on send). */
  selectedMode: ConversationType | null;
  /** Tap-to-select handler for modes that need the user to type a
   *  follow-up message before sending (Standard, Walking tour). */
  onSelectMode: (mode: ConversationType | null) => void;
  /** Tap-to-start handler for modes where the AI should begin the
   *  conversation immediately without the user typing anything
   *  (Museum guide). When the user taps a card wired up this way,
   *  the parent kicks off a synthetic first turn — creating the
   *  conversation in the right mode and asking the AI to greet them
   *  via the existing state-machine prompt. */
  onCommit: (mode: ConversationType) => void;
}

interface ModeCardSpec {
  mode: ConversationType;
  icon: React.ReactNode;
  /** Title + description go through useT so they're translatable. */
  titleKey: string;
  descKey: string;
}

const MODE_CARDS: ModeCardSpec[] = [
  {
    mode: 'standard',
    icon: <ChatBubbleOutline />,
    titleKey: 'Standard',
    descKey:
      "Just chat. Ask about where you are, snap a photo, or talk through what you're looking at.",
  },
  {
    mode: 'walking_tour',
    icon: <MapOutlined />,
    titleKey: 'Walking tour',
    descKey:
      "Tap to start. I'll find walking-tour ideas near you and ask which one sounds good \u2014 then plot the route.",
  },
  {
    mode: 'museum_guide',
    icon: <AccountBalanceOutlined />,
    titleKey: 'Museum guide',
    descKey:
      "Tap to start. I'll find the museums near you and ask which one you're at \u2014 then guide you through highlights or just ID exhibits as you photograph them.",
  },
  {
    mode: 'map_exploration',
    icon: <SearchOutlined />,
    titleKey: 'Find places',
    descKey:
      "Type what you're looking for \u2014 \u201Cramen near me,\u201D \u201Copen coffee shops,\u201D \u201Cart nouveau caf\u00E9s.\u201D I'll put real places on the map, ranked for you. Keep chatting to refine.",
  },
];

const ModeCard: React.FC<{
  spec: ModeCardSpec;
  selected: boolean;
  onClick: () => void;
}> = ({ spec, selected, onClick }) => {
  const t = useT();
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      // Tap target reaches the full card width so the touch surface is
      // generous. Keep the visual a card, not a chip.
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderRadius: 2,
        // 2px to lift selection clearly without making the unselected
        // state look ringed-in.
        border: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': {
          borderColor: selected ? 'primary.main' : 'text.secondary',
          bgcolor: selected ? 'action.selected' : 'action.hover',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: selected ? 'primary.main' : 'action.hover',
            color: selected ? 'primary.contrastText' : 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {spec.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {t(spec.titleKey)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {t(spec.descKey)}
          </Typography>
        </Box>
      </Box>
    </ButtonBase>
  );
};

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  selectedMode,
  onSelectMode,
  onCommit,
}) => {
  const t = useT();

  const handleCardClick = (mode: ConversationType) => {
    // Museum guide and Walking tour both auto-fire a kickoff turn so
    // the AI takes the lead immediately:
    //   • Museum guide → "Welcome! Which museum?" with nearby chips
    //   • Walking tour → "Welcome! Here are some ideas near you" with
    //     local route suggestions as chips
    // The user shouldn't need to type anything to start. The kickoff
    // path is implemented by the parent via onCommit.
    if (mode === 'museum_guide' || mode === 'walking_tour') {
      onCommit(mode);
      return;
    }

    // Standard and map_exploration both keep the type-then-send
    // pattern. For map_exploration, the user's first message IS
    // the search query — there's no clarification phase the AI
    // needs to drive (unlike walking tour, where the user might
    // start with vague intent like "let's tour Berlin"). Tap-to-
    // toggle selection; the user types and sends.
    if (selectedMode === mode) {
      onSelectMode(null);
    } else {
      onSelectMode(mode);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Padding scaled to viewport so on small phones the cards
        // breathe; on tablets/desktop the column doesn't sprawl.
        px: { xs: 2, sm: 4 },
        // Clear the floating frosted chrome so vertical centering
        // reflects the *usable* area (between header and composer
        // cluster + bottom nav) rather than the full column. Without
        // this, content centers in 100dvh but the bottom ~220px is
        // covered by the composer cluster — content reads low.
        // 220px ≈ composer + suggestions strip height (matches the
        // VirtualizedMessageList Footer spacer).
        paddingTop: 'calc(var(--app-header-h) + 24px)',
        paddingBottom: 'calc(var(--app-bottom-nav-h) + 220px + 24px)',
        overflowY: 'auto',
      }}
    >
      <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 480 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('How do you want to start?')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Pick a mode, then type your first message.')}
          </Typography>
        </Box>

        <Stack spacing={1.5}>
          {MODE_CARDS.map((spec) => (
            <ModeCard
              key={spec.mode}
              spec={spec}
              selected={selectedMode === spec.mode}
              onClick={() => handleCardClick(spec.mode)}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
